/**
 * @author      Shivani Mehra
 * @date        Aug, 2024
 * @copyright   www.ibirdsservices.com
 */

import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Form,
  Container,
  Row,
  Col,
  Card,
  Button,
  Overlay,
  Popover,
} from "react-bootstrap";
import WhatsAppChat from "./WhatsAppChat";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import Select from "react-select";
import MessageTemplateModal from "./MessageTemplateModal";
import jwt_decode from "jwt-decode";
import { MultiSelect } from "react-multi-select-component";
import { BsFilter } from "react-icons/bs";
import { FaThumbtack } from "react-icons/fa";
import moment from "moment";
import * as constants from "../../constants/CONSTANT";
import QuickRepliesTemplateModel from "./QuickRepliesTemplateModel";

const CHAT_PAGE_SIZE = 50;

/** Only real tag UUIDs; react-select may use temp keys like `new_tag_<ts>` before save — never send those to conversations API. */
const TAG_ID_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function filterConversationTagIds(tags) {
  return (tags || [])
    .map((t) => String(t?.value ?? "").trim())
    .filter((id) => TAG_ID_UUID.test(id));
}

const WhatsAppMessenger = ({ socket, selectedWhatsAppSetting }) => {
  const [body, setBody] = useState([]);
  const [showHide, setShowHide] = useState(false);
  const [userData, setUserData] = useState();
  const [filters, setFilters] = useState({
    recordType: "recentlyMessage",
  });
  const [receivedMessage, setReceivedMessage] = useState();
  const [showHideModal, setShowHideModal] = useState(false);
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token")),
  );
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const hasInteractiveMessageModule =
    userInfo?.modules?.some((module) => module.url === "interactive_message") ||
    false;
  // Helper function to format phone numbers based on user permissions
  const formatPhoneNumber = (number) => {
    const shouldHideNumber =
      userInfo?.userrole === "USER" &&
      userInfo?.is_whatsapp_number_hidden === true;

    if (shouldHideNumber && number) {
      const numberString = String(number);
      if (numberString.length <= 5) {
        return numberString;
      }
      const visibleDigits = numberString.slice(-5);
      const maskedDigits = "X".repeat(numberString.length - 5);
      return `${maskedDigits}${visibleDigits}`;
    }
    return number;
  };

  const hasTagsModule =
    userInfo?.modules?.some((module) => module.url === "tags") || false;
  const [bgColors, setBgColors] = useState([
    "#d3761f",
    "#00ad5b",
    "#debf31",
    "#239dd1",
    "#b67eb1",
    "#d3761f",
    "#de242f",
  ]);
  let colIndex = 0;
  const [cities, setCities] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [pinLoading, setPinLoading] = useState({});

  // Tag color palette
  const tagColors = [
    "#e6d0f7",
    "#b7c7ce",
    "#f7e6d0",
    "#d0f7e6",
    "#d0e6f7",
    "#f7d0d0",
    "#f7f3d0",
    "#d0f7f3",
    "#f3d0f7",
    "#d0f3f7",
  ];
  const [activeFilter, setActiveFilter] = useState("All");
  const [allTags, setAllTags] = useState([]);
  /** Committed filters sent to API */
  const [appliedTextName, setAppliedTextName] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [appliedTags, setAppliedTags] = useState([]);
  const [appliedTagFilterLogic, setAppliedTagFilterLogic] = useState("OR");
  /** Draft values inside tag popover until Apply */
  const [pendingTags, setPendingTags] = useState([]);
  const [pendingTagFilterLogic, setPendingTagFilterLogic] = useState("OR");
  const [showTagFilterPopover, setShowTagFilterPopover] = useState(false);
  const filterButtonRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [selectedConversations, setSelectedConversations] = useState([]);
  const [selectAllConversations, setSelectAllConversations] = useState(false);
  const [sendingQuickReplies, setSendingQuickReplies] = useState(false);
  const [showBulkQuickReplyModal, setShowBulkQuickReplyModal] = useState(false);
  const inFlightKeyRef = useRef(null);
  const lastCompletedKeyRef = useRef(null);
  const tableContainerRef = useRef(null);
  const chatListNextOffsetRef = useRef(0);
  const chatListHasMoreRef = useRef(false);
  const [chatListHasMore, setChatListHasMore] = useState(false);
  const [chatListTotal, setChatListTotal] = useState(null);
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState(false);

  /** Display full_number from API with same masking rules as local number. */
  const formatFullNumberDisplay = (item) => {
    const raw = item?.full_number;
    if (raw == null || String(raw).trim() === "") return "";
    return formatPhoneNumber(String(raw).replace(/^\+/, ""));
  };

  /** Short time for chat list (WhatsApp-like: time today, else date). */
  const formatListLastMessageTime = (value) => {
    if (!value) return "";
    const m = moment(value);
    if (!m.isValid()) return "";
    const now = moment();
    if (m.isSame(now, "day")) return m.format("HH:mm");
    if (m.isSame(now.clone().subtract(1, "day"), "day")) return "Yesterday";
    if (m.isSame(now, "year")) return m.format("D MMM");
    return m.format("D MMM YY");
  };

  const buildFetchKey = useCallback(
    ({ textName, recordType, tags, tagLogic }) => {
      const tagIds = filterConversationTagIds(tags).sort();
      return JSON.stringify({
        textName: textName || "",
        recordType: recordType || "",
        selectedWhatsAppSetting: selectedWhatsAppSetting || "",
        tagIds,
        tagFilterLogic: tagLogic || "OR",
      });
    },
    [selectedWhatsAppSetting],
  );

  const convertToCSV = (array) => {
    if (!array || array.length === 0) return "";

    const headers = Object.keys(array[0]);
    const csvRows = [
      headers.join(","),
      ...array.map((row) =>
        headers
          .map((field) => {
            const value = row[field]
              ? String(row[field]).replace(/"/g, '""')
              : "";
            return `"${value}"`;
          })
          .join(","),
      ),
    ];
    return csvRows.join("\n");
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const response = await WhatsAppAPI.exportMessageHistory(
        selectedWhatsAppSetting,
      );

      if (!response.success || !response.records?.length) {
        toast.warning("No records found.");
        setLoading(false);
        return;
      }

      const formatted = response.records
        .filter((msg) => {
          const hasMessage = !!msg.chatmsg?.trim();
          const hasTemplate = !!msg.template_name?.trim();
          const hasFile = !!msg.file_title?.trim();
          return hasMessage || hasTemplate || hasFile;
        })
        .map((msg) => ({
          Name: msg.name,
          To_Number: formatPhoneNumber(msg.to_number),
          Business_Number: msg.business_number,
          Message: msg.chatmsg,
          Template_Name: msg.template_name,
          Status: msg.status,
          Created_Date: moment(msg.createddate).format("DD-MM-YYYY HH:mm:ss"),
          File_URL: msg.file_title
            ? `${constants.FRONTEND_URL}/public/${userInfo.tenantcode}/attachment/${msg.file_title}`
            : "",
        }));

      if (!formatted.length) {
        alert("No valid records to export.");
        setLoading(false);
        return;
      }

      const csv = convertToCSV(formatted);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `message_history_${moment().format("DDMMYYYY_HHmm")}.csv`;
      link.click();
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const getPinId = (item) => {
    return item.lead_id;
  };

  const getChatRowDedupeKey = (record, recordType) => {
    if (recordType === "recentlyMessage") {
      return String(record.lead_id || record.id || "");
    }
    return String(record.id || "");
  };

  const canPin = (item) => {
    if (["lead", "unread", "archived"].includes(filters.recordType))
      return !!item.id;
    if (filters.recordType === "recentlyMessage")
      return !!(item.lead_id || item.id);
    return false;
  };

  const handlePinToggle = async (item) => {
    const pinId = getPinId(item);
    if (!pinId || pinLoading[pinId]) return;
    setPinLoading((prev) => ({ ...prev, [pinId]: true }));

    try {
      if (item.pinned) {
        await WhatsAppAPI.unpinLead(pinId);
      } else {
        await WhatsAppAPI.pinLead(pinId);
      }
      const rt = filters.recordType;
      const itemKey = getChatRowDedupeKey(item, rt);
      const nextPinned = !item.pinned;
      setBody((prev) => {
        const updated = (prev || []).map((row) =>
          getChatRowDedupeKey(row, rt) === itemKey
            ? { ...row, pinned: nextPinned }
            : row,
        );
        return [...updated].sort((a, b) => {
          const ap = a.pinned ? 1 : 0;
          const bp = b.pinned ? 1 : 0;
          if (ap !== bp) return bp - ap;
          const ta = Date.parse(a.last_message_time) || 0;
          const tb = Date.parse(b.last_message_time) || 0;
          return tb - ta;
        });
      });
    } catch (error) {
      console.error("Error toggling pin:", error);
    } finally {
      setPinLoading((prev) => ({ ...prev, [pinId]: false }));
    }
  };

  // Main data fetching function
  const fetchData = useCallback(
    async (isFilterChange = false, override = {}, opts = {}) => {
      const append = opts.append === true;
      const force = opts.force === true;
      const textName =
        override.textName !== undefined ? override.textName : appliedTextName;
      const recordType =
        override.recordType !== undefined
          ? override.recordType
          : filters.recordType;
      const tagsForApi =
        override.appliedTags !== undefined ? override.appliedTags : appliedTags;
      const tagLogic =
        override.tagFilterLogic !== undefined
          ? override.tagFilterLogic
          : appliedTagFilterLogic;

      // Don't fetch if there's no recordType
      if (!recordType) {
        setBody([]);
        return;
      }

      const baseKey = buildFetchKey({
        textName,
        recordType,
        tags: tagsForApi,
        tagLogic,
      });
      const reqKey = append
        ? `${baseKey}|offset:${chatListNextOffsetRef.current}|append`
        : baseKey;

      // Same filters already loaded successfully — skip (e.g. React Strict Mode double mount)
      if (!append && !force && lastCompletedKeyRef.current === baseKey) {
        return;
      }

      if (inFlightKeyRef.current === reqKey) return;
      inFlightKeyRef.current = reqKey;

      if (!append) {
        chatListNextOffsetRef.current = 0;
        chatListHasMoreRef.current = true;
        setChatListHasMore(true);
        setChatListTotal(null);
        if (isFilterChange) {
          setIsFilterLoading(true);
        }
      } else {
        if (!chatListHasMoreRef.current) {
          inFlightKeyRef.current = null;
          return;
        }
        setIsLoadingMoreChats(true);
      }

      const tagIdsForApi = filterConversationTagIds(tagsForApi);

      try {
        const offset = append ? chatListNextOffsetRef.current : 0;
        const result = await WhatsAppAPI.getConversations({
          textName,
          recordType,
          limit: CHAT_PAGE_SIZE,
          offset,
          tagIds: tagIdsForApi,
          tagFilterLogic: tagLogic,
        });

        if (result.success) {
          const records = result.records || [];
          const formattedChunk = records.map((record) => ({
            ...record,
            display_whatsapp_number: formatPhoneNumber(record.full_number),
          }));

          const total = typeof result.total === "number" ? result.total : null;
          const hasMoreFlag =
            typeof result.hasMore === "boolean"
              ? result.hasMore
              : formattedChunk.length >= CHAT_PAGE_SIZE;

          if (!append) {
            setBody(formattedChunk);
            setChatListTotal(total);
          } else {
            setBody((prev) => {
              const seen = new Set(
                (prev || []).map((r) => getChatRowDedupeKey(r, recordType)),
              );
              const merged = [...prev];
              for (const row of formattedChunk) {
                const key = getChatRowDedupeKey(row, recordType);
                if (!key || seen.has(key)) continue;
                seen.add(key);
                merged.push(row);
              }
              return merged;
            });
          }

          chatListNextOffsetRef.current = offset + formattedChunk.length;
          chatListHasMoreRef.current = hasMoreFlag;
          setChatListHasMore(hasMoreFlag);
          lastCompletedKeyRef.current = baseKey;
        } else {
          if (!append) {
            setBody([]);
            setChatListTotal(0);
          }
          chatListHasMoreRef.current = false;
          setChatListHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (!append) {
          setBody([]);
          setChatListTotal(0);
        }
        chatListHasMoreRef.current = false;
        setChatListHasMore(false);
      } finally {
        inFlightKeyRef.current = null;
        if (!append) {
          if (isFilterChange) {
            setIsFilterLoading(false);
          }
        } else {
          setIsLoadingMoreChats(false);
        }
      }
    },
    [
      appliedTextName,
      filters.recordType,
      buildFetchKey,
      appliedTags,
      appliedTagFilterLogic,
    ],
  );

  /** Tab/account effect must not depend on `fetchData` identity (it changes when applied search/tags change). */
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  const applySearchFilters = useCallback(() => {
    const next = searchDraft.trim();
    setAppliedTextName(next);
    // Pass textName explicitly so this request does not race with stale state or a second effect fetch
    fetchData(true, { textName: next }, { force: true });
  }, [searchDraft, fetchData]);

  /** Clear applied search text and refetch — clearing the input alone does not update the API until this runs. */
  const clearSearchFilters = useCallback(() => {
    setSearchDraft("");
    setAppliedTextName("");
    fetchData(true, { textName: "" }, { force: true });
  }, [fetchData]);

  const applyTagFilters = useCallback(() => {
    const mustUseLead =
      pendingTags.length > 0 &&
      !["lead", "recentlyMessage", "unread", "archived"].includes(
        filters.recordType,
      );
    if (mustUseLead) {
      setFilters((f) => ({ ...f, recordType: "lead" }));
    }
    setAppliedTags(pendingTags);
    setAppliedTagFilterLogic(pendingTagFilterLogic);
    setShowTagFilterPopover(false);
    if (mustUseLead) {
      // useEffect on recordType will fetch with updated applied tags (same batch)
      return;
    }
    fetchData(
      true,
      {
        appliedTags: pendingTags,
        tagFilterLogic: pendingTagFilterLogic,
      },
      { force: true },
    );
  }, [pendingTags, pendingTagFilterLogic, filters.recordType, fetchData]);

  const handleChatListScroll = useCallback(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    if (isFilterLoading || isLoadingMoreChats) return;
    if (!chatListHasMore) return;

    const thresholdPx = 120;
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceToBottom > thresholdPx) return;

    fetchData(false, {}, { append: true });
  }, [
    isFilterLoading,
    isLoadingMoreChats,
    filters.recordType,
    chatListHasMore,
    fetchData,
  ]);

  // Socket listener for new messages
  useEffect(() => {
    socket?.on("receivedwhatsappmessage", (data) => {
      console.log(data);
      setReceivedMessage(data);
    });

    return () => {
      socket?.off("receivedwhatsappmessage");
    };
  }, [socket]);

  // Main data fetching: tab (record type) or WhatsApp account only — not on typing/tags until Apply
  useEffect(() => {
    fetchDataRef.current(true);
  }, [filters.recordType, selectedWhatsAppSetting]);

  // Initialize record type based on user modules
  useEffect(() => {
    if (!filters.recordType) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        recordType: userInfo?.modules?.some((module) => module.url === "leads")
          ? "lead"
          : "user",
      }));
    }
  }, [filters, userInfo?.modules]);

  // Tag options for filter (from tags API — not from conversation list)
  useEffect(() => {
    const fetchAllTags = async () => {
      try {
        const res = await WhatsAppAPI.getTags();
        if (res.success && Array.isArray(res.records)) {
          setAllTags(
            res.records.map((tag) => ({
              value: tag.id,
              label: tag.name,
            })),
          );
        }
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };

    if (hasTagsModule) {
      fetchAllTags();
    }
  }, [hasTagsModule]);

  const handleFilterButtonClick = useCallback((filterValue) => {
    // Clear data immediately
    setBody([]);
    setShowHide(false);
    setSelectedItems([]);
    setSelectAll(false);
    setSelectedTag(null);

    // Clear tags when switching to a filter that doesn't support tags
    if (
      filterValue !== "lead" &&
      filterValue !== "recentlyMessage" &&
      filterValue !== "unread" &&
      filterValue !== "archived"
    ) {
      setAppliedTags([]);
      setPendingTags([]);
    }

    // Update filter
    setFilters((f) => ({
      ...f,
      recordType: filterValue,
    }));
  }, []);

  const handleUserClick = (item) => {
    setShowHide(true);
    setUserData(item);

    const unreadCount = Number(item.unread_count) || 0;
    if (item.full_number && unreadCount > 0) {
      markMessagesAsRead(item.full_number, item);
    }
  };

  const markMessagesAsRead = async (full_number, item) => {
    try {
      let result = await WhatsAppAPI.markMessagesAsRead(
        full_number,
        selectedWhatsAppSetting,
      );
      if (result.success && item) {
        const rt = filters.recordType;
        const itemKey = getChatRowDedupeKey(item, rt);
        setBody((prev) =>
          prev.map((row) =>
            getChatRowDedupeKey(row, rt) === itemKey
              ? { ...row, unread_count: "0" }
              : row,
          ),
        );
      }
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const handleSelectAll = () => {
    if (!selectAll) {
      setSelectedItems([...body]);
    } else {
      setSelectedItems([]);
    }
    setSelectAll(!selectAll);
  };

  const handleCheckboxChange = (event, item) => {
    if (event.target.checked) {
      setSelectedItems((prevSelected) => [...prevSelected, item]);
    } else {
      setSelectedItems((prevSelected) =>
        prevSelected.filter((selectedItem) => selectedItem.id !== item.id),
      );
    }
  };

  const isAllSelected = selectedItems.length === body.length;

  const sendMessagesBulk = (e) => {
    setShowHide(false);
    if (selectedItems.length > 0) {
      if (selectedItems[0].full_number) {
        setShowHideModal(true);
      } else {
        if (selectedItems.length === 1) {
          setShowHideModal(true);
        } else {
          toast.error("Please select only one group at a time.");
        }
      }
    } else {
      toast.error("Please select at least one item.");
    }
  };

  const refreshData = () => {
    fetchData(true, {}, { force: true });
    setShowHideModal(false);
    setSelectedItems([]);
    setSelectAll(false);
  };

  /** Update sidebar list when a chat is archived/unarchived — avoids refetching the whole conversations API. */
  const patchConversationListAfterArchive = useCallback(
    (leadId, isArchived) => {
      if (leadId == null || leadId === "") return;
      const sid = String(leadId);
      const rt = filters.recordType;

      const matches = (row) => {
        if (rt === "recentlyMessage") {
          return String(row.lead_id || row.id) === sid;
        }
        return String(row.lead_id || "") === sid || String(row.id) === sid;
      };

      const shouldRemove =
        (isArchived &&
          (rt === "lead" || rt === "unread" || rt === "recentlyMessage")) ||
        (!isArchived && rt === "archived");

      setBody((prev) => {
        if (!prev?.length) return prev;
        if (shouldRemove) {
          const next = prev.filter((row) => !matches(row));
          if (next.length < prev.length) {
            queueMicrotask(() => {
              setChatListTotal((t) =>
                typeof t === "number" ? Math.max(0, t - 1) : t,
              );
            });
          }
          return next;
        }
        return prev.map((row) =>
          matches(row) ? { ...row, is_archived: isArchived } : row,
        );
      });
      lastCompletedKeyRef.current = null;
    },
    [filters.recordType],
  );

  const fillBgColor = () => {
    colIndex += 1;
    if (colIndex >= bgColors.length) colIndex = 0;
    return bgColors[colIndex];
  };

  const clearTagFilter = useCallback(() => {
    setAppliedTags([]);
    setPendingTags([]);
    setAppliedTagFilterLogic("OR");
    setPendingTagFilterLogic("OR");
    setShowTagFilterPopover(false);
    // Explicit overrides — do not use setTimeout(fetchData): stale closure kept old tagIds/textName in the request
    fetchDataRef.current(
      true,
      { appliedTags: [], tagFilterLogic: "OR" },
      { force: true },
    );
  }, []);

  // Determine if we should show loading
  const showLoading = isFilterLoading;

  const filterData = useMemo(
    () => ({
      ...filters,
      textName: appliedTextName,
    }),
    [filters, appliedTextName],
  );

  const showPinColumn = [
    "lead",
    "recentlyMessage",
    "unread",
    "archived",
  ].includes(filters.recordType);

  const chatListColSpan =
    (hasInteractiveMessageModule ? 1 : 0) + 2 + (showPinColumn ? 1 : 0);

  // Handle individual conversation selection
  const handleConversationSelect = (item, event) => {
    event.stopPropagation(); // Prevent row click

    setSelectedConversations((prev) => {
      const exists = prev.some(
        (conv) =>
          conv.id === item.id ||
          (filters.recordType === "recentlyMessage" &&
            conv.lead_id === item.lead_id),
      );

      if (exists) {
        return prev.filter(
          (conv) =>
            conv.id !== item.id &&
            (filters.recordType !== "recentlyMessage" ||
              conv.lead_id !== item.lead_id),
        );
      } else {
        return [...prev, item];
      }
    });
  };

  // Handle select all conversations
  const handleSelectAllConversations = () => {
    if (!selectAllConversations) {
      setSelectedConversations([...body]);
    } else {
      setSelectedConversations([]);
    }
    setSelectAllConversations(!selectAllConversations);
  };

  // Clear all selections
  const clearSelections = () => {
    setSelectedConversations([]);
    setSelectAllConversations(false);
  };

  // Check if a conversation is selected
  const isConversationSelected = (item) => {
    return selectedConversations.some(
      (conv) =>
        conv.id === item.id ||
        (filters.recordType === "recentlyMessage" &&
          conv.lead_id === item.lead_id),
    );
  };

  // Update selectAllConversations when body changes
  useEffect(() => {
    if (selectedConversations.length === body.length && body.length > 0) {
      setSelectAllConversations(true);
    } else {
      setSelectAllConversations(false);
    }
  }, [selectedConversations, body]);
  return (
    <>
      {/* <Container className="mt-5">
        <Row className="g-0 mx-5 text-center">
          <Col lg={12} xs={12} sm={12}>
            <div
              className=" text-center p-2"
              style={{
                height: "40px",
                backgroundColor: "#ffffff",
                borderRadius: "5px",
              }}
            >
              <span
                className="fw-semibold p-1"
                style={{ color: "#605C68", fontSize: "large" }}
              >
                WhatsApp for Windows
              </span>
            </div>
          </Col>
        </Row>
      </Container> */}

      <Container className="mt-2 mb-3">
        <Row className="g-0 mx-5">
          <Col lg={5} sm={12} xs={12} className="mb-3">
            <Card className="h-100">
              <Card.Header className="p-3 header-bg">
                <Row className="g-0 ">
                  <Col
                    lg={6}
                    xs={6}
                    sm={6}
                    style={{ display: "flex", alignItems: "stretch" }}
                  >
                    <i
                      className="fa-brands fa-whatsapp"
                      style={{ fontSize: "30px", color: "#128C7E" }}
                    ></i>
                    <span
                      className="mx-3 mt-1 fw-bold"
                      style={{
                        fontSize: "15px",
                        color: "var(--text-color-2)",
                      }}
                    >
                      {userInfo.username}
                    </span>
                  </Col>
                  <Col lg={6} xs={6} sm={6} className="text-end">
                    {selectedConversations.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => setShowBulkQuickReplyModal(true)}
                        disabled={sendingQuickReplies || showLoading}
                        title={`Send quick reply to ${selectedConversations.length} selected conversation(s)`}
                      >
                        {sendingQuickReplies ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane me-1"></i>
                            Send to {selectedConversations.length}
                          </>
                        )}
                      </button>
                    )}
                    {(userInfo.userrole !== "USER" ||
                      userInfo.can_export_leads === true) && (
                      <button
                        type="button"
                        className="btn btn-sm white-btn"
                        onClick={handleExport}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Exporting...
                          </>
                        ) : (
                          "Export History"
                        )}
                      </button>
                    )}
                  </Col>
                </Row>
              </Card.Header>

              <Card.Body className="mb-5">
                <div style={{ height: "30rem" }}>
                  <Row className="g-0">
                    <Col lg={12} sm={12} xs={12}>
                      <Row className="mt-2 g-2 align-items-center mb-3">
                        <Col xs={12} sm>
                          <Form.Control
                            style={{ height: "38px" }}
                            type="text"
                            placeholder="Search by name or WhatsApp number..."
                            value={searchDraft}
                            onChange={(e) => setSearchDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") applySearchFilters();
                            }}
                            disabled={isFilterLoading}
                          />
                        </Col>
                        <Col xs="auto" className="d-flex gap-1">
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            style={{ height: "38px" }}
                            onClick={applySearchFilters}
                            disabled={isFilterLoading}
                          >
                            Filter
                          </Button>
                          {(searchDraft.trim() !== "" ||
                            appliedTextName.trim() !== "") && (
                            <Button
                              type="button"
                              variant="outline-secondary"
                              size="sm"
                              style={{ height: "38px" }}
                              onClick={clearSearchFilters}
                              disabled={isFilterLoading}
                              title="Clear search and reload list"
                            >
                              Clear
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </Col>

                    {/* Filter bar — wrap instead of horizontal scroll */}
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                        marginBottom: "10px",
                        alignItems: "center",
                        rowGap: "8px",
                      }}
                    >
                      {[
                        { label: "All", value: "lead" },
                        { label: "New Messages", value: "recentlyMessage" },
                        { label: "Unread", value: "unread" },
                        { label: "archived", value: "archived" },
                      ].map((filter) => (
                        <button
                          key={filter.value}
                          type="button"
                          className={`btn btn-sm ${
                            filters.recordType === filter.value && !selectedTag
                              ? "btn-primary"
                              : "btn-outline-secondary"
                          }`}
                          style={{
                            borderRadius: "5px",
                            minWidth: 0,
                            paddingLeft: "10px",
                            paddingRight: "10px",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => handleFilterButtonClick(filter.value)}
                          disabled={isFilterLoading} // Only disable for filter changes
                        >
                          {filter.label}
                          {isFilterLoading &&
                            filters.recordType === filter.value && (
                              <span
                                className="ms-2 spinner-border spinner-border-sm"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </span>
                            )}
                        </button>
                      ))}

                      {hasTagsModule && (
                        <div style={{ position: "relative" }}>
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              appliedTags.length > 0
                                ? "btn-primary"
                                : "btn-outline-secondary"
                            }`}
                            style={{
                              borderRadius: "5px",
                              minWidth: "80px",
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                            }}
                            ref={filterButtonRef}
                            onClick={() => {
                              if (!showTagFilterPopover) {
                                setPendingTags(appliedTags);
                                setPendingTagFilterLogic(appliedTagFilterLogic);
                              }
                              setShowTagFilterPopover((o) => !o);
                            }}
                            disabled={isFilterLoading}
                          >
                            <BsFilter style={{ marginRight: 2 }} />
                            {appliedTags.length > 0
                              ? `${appliedTags.length} Tags`
                              : "Tags"}
                          </button>
                        </div>
                      )}

                      {appliedTags.length > 0 &&
                        appliedTags.map((tag) => (
                          <span
                            key={tag.value}
                            className="badge bg-light text-dark border"
                            style={{
                              borderRadius: "0.375rem",
                              padding: "4px 8px",
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              backgroundColor: "#f0f0f0",
                              border: "1px solid #ccc",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {tag.label}
                            <button
                              type="button"
                              className="btn btn-sm p-0"
                              style={{
                                border: "none",
                                background: "none",
                                fontSize: "10px",
                                color: "#666",
                                cursor: "pointer",
                                padding: "0",
                                marginLeft: "2px",
                              }}
                              onClick={() => {
                                const next = appliedTags.filter(
                                  (t) => t.value !== tag.value,
                                );
                                setAppliedTags(next);
                                setPendingTags(next);
                                fetchDataRef.current(
                                  true,
                                  {
                                    appliedTags: next,
                                    tagFilterLogic: appliedTagFilterLogic,
                                  },
                                  { force: true },
                                );
                              }}
                              title="Remove tag"
                              disabled={isFilterLoading}
                            >
                              ×
                            </button>
                          </span>
                        ))}

                      {appliedTags.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          style={{
                            fontSize: "0.75rem",
                            padding: "2px 6px",
                            borderRadius: "0.375rem",
                            whiteSpace: "nowrap",
                          }}
                          onClick={clearTagFilter}
                          title="Clear all tags"
                          disabled={isFilterLoading}
                        >
                          Clear All
                        </button>
                      )}
                    </div>

                    <hr />

                    <div
                      ref={tableContainerRef}
                      className="table-container"
                      style={{
                        maxHeight: "25rem",
                        overflowY: "auto",
                        overflowX: "hidden",
                        width: "100%",
                        maxWidth: "100%",
                      }}
                      onScroll={handleChatListScroll}
                    >
                      <Col lg={12} sm={12} xs={12}>
                        {showLoading ? (
                          <div className="text-center py-5">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="mt-2">Loading conversations...</p>
                          </div>
                        ) : body.length > 0 ? (
                          <table
                            className="table mb-0"
                            style={{
                              tableLayout: "fixed",
                              width: "100%",
                            }}
                          >
                            <colgroup>
                              {hasInteractiveMessageModule && (
                                <col style={{ width: 34 }} />
                              )}
                              <col />
                              <col style={{ width: 52 }} />
                              {showPinColumn && <col style={{ width: 108 }} />}
                            </colgroup>
                            <thead>
                              <tr>
                                {hasInteractiveMessageModule && (
                                  <th
                                    className="align-middle p-1 ps-2 pe-0"
                                    style={{ width: 34 }}
                                  >
                                    <Form.Check
                                      type="checkbox"
                                      checked={
                                        selectAllConversations &&
                                        body.length > 0
                                      }
                                      onChange={handleSelectAllConversations}
                                      disabled={
                                        showLoading || body.length === 0
                                      }
                                      title="Select all"
                                    />
                                  </th>
                                )}
                                <th className="px-2 text-nowrap">
                                  Conversations
                                </th>
                                <th
                                  className="px-1 text-end text-nowrap"
                                  style={{ width: 52 }}
                                ></th>
                                {showPinColumn && (
                                  <th
                                    className="px-1 text-end text-nowrap"
                                    style={{ width: 108 }}
                                  ></th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {body.map((item) => {
                                const unreadCount =
                                  Number(item.unread_count) || 0;

                                return (
                                  <tr
                                    key={
                                      filters.recordType === "recentlyMessage"
                                        ? item.lead_id || item.id
                                        : item.id
                                    }
                                  >
                                    {hasInteractiveMessageModule && (
                                      <td
                                        className="align-middle p-1 ps-2 pe-0"
                                        style={{
                                          width: 34,
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        <Form.Check
                                          type="checkbox"
                                          checked={isConversationSelected(item)}
                                          onChange={(e) =>
                                            handleConversationSelect(item, e)
                                          }
                                          disabled={showLoading}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </td>
                                    )}
                                    <td
                                      onClick={() => handleUserClick(item)}
                                      style={{
                                        cursor: "pointer",
                                        maxWidth: 0,
                                        verticalAlign: "middle",
                                      }}
                                      className="align-middle ps-1 pe-2"
                                    >
                                      <div className="d-flex align-items-center gap-2">
                                        <NameInitialsAvatar
                                          size="40px"
                                          textSize="14px"
                                          bgColor={fillBgColor()}
                                          borderWidth="0px"
                                          textColor="#fff"
                                          name={item?.contactname}
                                        />
                                        <div
                                          className="d-flex flex-column justify-content-center flex-grow-1"
                                          style={{ minWidth: 0 }}
                                        >
                                          <span
                                            className="text-capitalize fw-semibold text-truncate"
                                            style={{
                                              fontSize: "16px",
                                              color: "#111b21",
                                            }}
                                          >
                                            {item.contactname}
                                          </span>
                                          {formatFullNumberDisplay(item) && (
                                            <span
                                              className="text-truncate"
                                              style={{
                                                fontSize: "13px",
                                                color: "#667781",
                                                marginTop: "2px",
                                              }}
                                            >
                                              {formatFullNumberDisplay(item)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="align-middle text-end pe-3">
                                      {unreadCount > 0 ? (
                                        <span
                                          title={`${unreadCount} unread`}
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            minWidth: unreadCount > 9 ? 24 : 22,
                                            height: 22,
                                            paddingLeft:
                                              unreadCount > 9 ? 6 : 0,
                                            paddingRight:
                                              unreadCount > 9 ? 6 : 0,
                                            borderRadius: 12,
                                            backgroundColor: "#25D366",
                                            color: "#fff",
                                            fontSize: 12,
                                            fontWeight: 600,
                                            lineHeight: 1,
                                            boxSizing: "border-box",
                                          }}
                                        >
                                          {unreadCount > 99
                                            ? "99+"
                                            : unreadCount}
                                        </span>
                                      ) : null}
                                    </td>
                                    {showPinColumn && (
                                      <td
                                        className="align-middle text-end pe-2"
                                        style={{ whiteSpace: "nowrap" }}
                                      >
                                        <div className="d-flex align-items-center justify-content-end gap-2">
                                          {item.last_message_time ? (
                                            <span
                                              className="text-muted small"
                                              style={{
                                                fontSize: "12px",
                                                color: "#667781",
                                              }}
                                              title={moment(
                                                item.last_message_time,
                                              ).format("LLL")}
                                            >
                                              {formatListLastMessageTime(
                                                item.last_message_time,
                                              )}
                                            </span>
                                          ) : null}
                                          {canPin(item) ? (
                                            <Button
                                              variant="link"
                                              className="p-0 m-0 flex-shrink-0"
                                              style={{
                                                color: item.pinned
                                                  ? "#007bff"
                                                  : "#aaa",
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handlePinToggle(item);
                                              }}
                                              disabled={
                                                pinLoading[getPinId(item)] ||
                                                showLoading
                                              }
                                              title={
                                                item.pinned ? "Unpin" : "Pin"
                                              }
                                            >
                                              <FaThumbtack
                                                style={
                                                  item.pinned
                                                    ? {}
                                                    : { opacity: 0.5 }
                                                }
                                              />
                                            </Button>
                                          ) : null}
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                              {isLoadingMoreChats && (
                                <tr>
                                  <td
                                    colSpan={chatListColSpan}
                                    className="text-center py-2 text-muted small"
                                  >
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                    Loading more…
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        ) : (
                          <div className="text-center py-5">
                            {appliedTextName
                              ? "No conversations found matching your search."
                              : "No conversations found."}
                          </div>
                        )}
                      </Col>
                    </div>
                  </Row>
                </div>
              </Card.Body>

              <Card.Footer>
                <Row className="g-0 mt-3">
                  <Col lg={6} sm={12} xs={12} className="mt-1">
                    <p>
                      <b>
                        {chatListTotal != null
                          ? `Loaded: ${body.length}${
                              chatListHasMore ? "+" : ""
                            } / ${chatListTotal}`
                          : `Total Records: ${body.length}`}
                      </b>
                    </p>
                  </Col>
                </Row>
              </Card.Footer>
            </Card>
          </Col>

          {showHide && (
            <Col lg={7} sm={12} xs={12} className="mb-3">
              <WhatsAppChat
                show={showHide}
                onHide={() => setShowHide(false)}
                userDetail={userData}
                filterData={filterData}
                socket={socket}
                selectedWhatsAppSetting={selectedWhatsAppSetting}
                onArchiveListChange={patchConversationListAfterArchive}
              />
            </Col>
          )}
        </Row>
      </Container>
      <ToastContainer autoClose={30000} />
      {/* Bulk Quick Reply Modal */}
      {showBulkQuickReplyModal && selectedConversations.length > 0 && (
        <QuickRepliesTemplateModel
          show={showBulkQuickReplyModal}
          onHide={() => {
            setShowBulkQuickReplyModal(false);
            clearSelections();
          }}
          contactData={selectedConversations}
          refreshData={refreshData}
          filterData={filterData}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
          isBulk={true}
          onBulkSendComplete={() => {
            setSendingQuickReplies(false);
            clearSelections();
          }}
        />
      )}
      {showHideModal && (
        <MessageTemplateModal
          show={showHideModal}
          onHide={() => setShowHideModal(false)}
          contactData={selectedItems}
          refreshData={refreshData}
          filterData={filterData}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
        />
      )}

      <Overlay
        show={showTagFilterPopover}
        target={filterButtonRef.current}
        placement="bottom"
        containerPadding={20}
        rootClose
        onHide={() => {
          setPendingTags(appliedTags);
          setPendingTagFilterLogic(appliedTagFilterLogic);
          setShowTagFilterPopover(false);
        }}
        trigger="click"
      >
        <Popover id="tag-filter-popover" style={{ minWidth: 400 }}>
          <Popover.Header
            as="h3"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Filter Leads by Tags</span>
            <div>
              {(appliedTags.length > 0 || pendingTags.length > 0) && (
                <Button
                  size="sm"
                  variant="link"
                  style={{
                    color: "#dc3545",
                    textDecoration: "none",
                    marginRight: "10px",
                  }}
                  onClick={clearTagFilter}
                  disabled={isFilterLoading}
                >
                  Clear
                </Button>
              )}
              <Button
                size="sm"
                variant="link"
                style={{
                  color: "var(--text-color-2)",
                  textDecoration: "none",
                }}
                onClick={() => {
                  setPendingTags(appliedTags);
                  setPendingTagFilterLogic(appliedTagFilterLogic);
                  setShowTagFilterPopover(false);
                }}
              >
                X
              </Button>
            </div>
          </Popover.Header>
          <Popover.Body style={{ minWidth: 400 }}>
            <div style={{ minWidth: 350, maxWidth: "100%" }}>
              <MultiSelect
                options={allTags}
                value={pendingTags}
                onChange={setPendingTags}
                labelledBy="Select Tags"
                overrideStrings={{
                  selectSomeItems: "Select tags...",
                  allItemsAreSelected: "All tags selected",
                  selectAll: "Select All",
                  search: "Search tags",
                }}
                style={{ minWidth: 350, maxWidth: "100%" }}
                disabled={isFilterLoading}
              />
            </div>
            <div className="mt-3">
              <Form.Check
                inline
                label="OR"
                type="radio"
                checked={pendingTagFilterLogic === "OR"}
                onChange={() => setPendingTagFilterLogic("OR")}
                disabled={isFilterLoading}
              />
              <Form.Check
                inline
                label="AND"
                type="radio"
                checked={pendingTagFilterLogic === "AND"}
                onChange={() => setPendingTagFilterLogic("AND")}
                disabled={isFilterLoading}
              />
            </div>
            {pendingTags.length > 0 && (
              <div className="mt-3">
                <strong>Selected Tags:</strong>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  {pendingTags.map((tag) => (
                    <span
                      key={tag.value}
                      className="badge bg-light text-dark border"
                      style={{
                        borderRadius: "0.375rem",
                        padding: "4px 8px",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        backgroundColor: "#f0f0f0",
                        border: "1px solid #ccc",
                        display: "flex",
                        alignItems: "center",
                        marginRight: 4,
                      }}
                    >
                      {tag.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-3 d-flex justify-content-end gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setPendingTags(appliedTags);
                  setPendingTagFilterLogic(appliedTagFilterLogic);
                  setShowTagFilterPopover(false);
                }}
                disabled={isFilterLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={applyTagFilters}
                disabled={isFilterLoading}
              >
                Apply filters
              </Button>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
};

export default WhatsAppMessenger;
